// Dataset-provider registry.
//
// The single dataset-resolution seam: a source op routes to
// one provider *group* (`query` → `'knowledge-graph'`, `datapoints` →
// `'timeseries'`); within a group, providers form a priority list and
// the first to `claims(name)` owns the dataset. CDF views register as
// one provider claiming every view externalId; facade/virtual providers
// register their own names.
//
// Pure data + lookup — no I/O, no evaluation. The registry is threaded
// onto both the runtime `EvalContext` (fetch dispatch by `providerId`)
// and the sync type-check ctx (`SourceSchemaCtx.getProvider`), so a
// dataset name resolves identically at static-check time and runtime.

import type { SourcePos } from '../ast.ts';
import { UnknownDatasetError } from '../errors.ts';
import type { DatasetProvider } from './types.ts';

/**
 * Resolve a dataset name within a group to a provider. The lookup
 * signature shared by `PlanCtx.getProvider`, `SourceSchemaCtx.getProvider`,
 * and `EvalContext` fetch dispatch — the SSOT for "which provider owns
 * `name`". Returns `undefined` when no provider in `group` claims the
 * name, so callers can degrade gracefully (the sync type-check pass) or
 * raise a precise `UnknownDatasetError` (the runtime op-apply path).
 */
export type ProviderResolver = (group: string, name: string) => DatasetProvider | undefined;

/**
 * Immutable provider registry. Built once per `(client, views)` by the
 * host (`ChatSessionProvider`) and mounted on the eval/check contexts.
 *
 * Groups map to an *ordered* provider list — order is priority, so a
 * specific facade can shadow a broad catch-all leaf. Resolution is a
 * linear first-`claims` scan within the requested group.
 */
export interface ProviderRegistry {
  /** The providers registered in `group`, in priority order. */
  readonly providers: (group: string) => readonly DatasetProvider[];
  /** First provider in `group` whose `claims(name)` is true, else `undefined`. */
  readonly getProvider: ProviderResolver;
  /**
   * Resolve a provider by its `id` across every group — the fetch-dispatch
   * lookup. A `ProviderSource` carries only `providerId` (not the group +
   * dataset name it was minted from), so the materialiser resolves the
   * owning provider here. Returns `undefined` when no registered provider
   * has that id, which the materialiser turns into a precise
   * missing-provider diagnostic.
   */
  readonly getProviderById: (id: string) => DatasetProvider | undefined;
  /**
   * Every dataset name claimable across `group`'s providers, used for
   * the `UnknownDatasetError` did-you-mean suggestion. A provider that
   * cannot enumerate its claimed names (a fuzzy CDF claim-all) omits
   * them — the suggestion list is best-effort.
   */
  readonly claimedNames: (group: string) => readonly string[];
  /**
   * First provider in `group` whose `claims(name)` is true, or throw a
   * precise `UnknownDatasetError` — the op-apply path uses this; the sync
   * type-check path uses `getProvider` and degrades to a no-op on
   * `undefined`.
   */
  readonly requireProvider: (group: string, name: string, pos: SourcePos) => DatasetProvider;
}

/**
 * One provider's optional enumeration of the names it claims — supplied
 * at registration so the registry can build a did-you-mean list without
 * a `claims`-probe oracle. Omitted for a provider that claims a name
 * space it cannot enumerate (e.g. a fuzzy catch-all).
 */
export type ProviderEntry = {
  readonly provider: DatasetProvider;
  /** Best-effort enumeration of claimed dataset names for diagnostics. */
  readonly names?: string[];
};

/**
 * Build a `ProviderRegistry` from a group → ordered entry-list map.
 * Entry order within a group is priority order (first-`claims` wins).
 * The map is copied defensively so the registry is an immutable
 * snapshot independent of the caller's mutable build-up.
 */
export const useProviderRegistry = (groups: Readonly<Record<string, readonly ProviderEntry[]>>): ProviderRegistry => {
  const byGroup = new Map<string, readonly ProviderEntry[]>(
    Object.entries(groups).map(([group, entries]) => [group, [...entries]] as const),
  );
  const entriesFor = (group: string): readonly ProviderEntry[] => byGroup.get(group) ?? [];
  const providers = (group: string): readonly DatasetProvider[] => entriesFor(group).map((e) => e.provider);
  const getProvider: ProviderResolver = (group, name) =>
    entriesFor(group).find((e) => e.provider.claims(name))?.provider;
  // Flat id → provider index across every group, built once. A provider
  // id is the dispatch + cache discriminator, so it must be unique across
  // the whole registry; a collision is a wiring bug (two providers fighting
  // over one `ProviderSource.providerId`) and fails loudly at build time.
  const byId = new Map<string, DatasetProvider>();
  for (const entries of byGroup.values()) {
    for (const { provider } of entries) {
      if (byId.has(provider.id)) {
        throw new Error(`duplicate provider id '${provider.id}' in the provider registry`);
      }
      byId.set(provider.id, provider);
    }
  }
  const getProviderById = (id: string): DatasetProvider | undefined => byId.get(id);
  const claimedNames = (group: string): readonly string[] => entriesFor(group).flatMap((e) => e.names ?? []);
  const requireProvider = (group: string, name: string, pos: SourcePos): DatasetProvider => {
    const provider = getProvider(group, name);
    if (provider === undefined) {
      throw new UnknownDatasetError(name, claimedNames(group), pos);
    }
    return provider;
  };
  return { providers, getProvider, getProviderById, claimedNames, requireProvider };
};
